import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { TemplateItemIcon, ChevronIcon, TrashIcon, PlusIcon } from '../icons';
import SprintTreeItem from './SprintTreeItem';
import ConfirmModal from '../../SprintDetail/FlowchartView/components/ConfirmModal';
import IconPicker from '../../SprintDetail/shared/IconPicker';
import { renderIcon } from '../../SprintDetail/FlowchartView/utils/renderIcon';

/**
 * TemplateTreeItem Component
 * Renders a template item with optional nested children (campaigns inside the template)
 * Supports icon picker and context menu like SprintTreeItem
 */
const TemplateTreeItem = ({
  template,
  nestedInTemplates = [],
  selectedTemplateId,
  selectedSprintId,
  expandedSprints,
  onToggleExpand,
  onSelectTemplate,
  onSelectSprint,
  onRenameTemplate,
  onTemplateIconChange,
  onDeleteTemplate,
  onDuplicateTemplate,
  onUseTemplate,
  onCreateNestedSprint,
  onRenameSprint,
  onSprintIconChange,
  onDeleteSprint,
  onOpenInNewTab,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(template.name);
  const [contextMenu, setContextMenu] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showIconPicker, setShowIconPicker] = useState(false);
  const [iconPickerPosition, setIconPickerPosition] = useState(null);
  
  const inputRef = useRef(null);
  const iconRef = useRef(null);

  const isSelected = selectedTemplateId === template.id;
  const hasIcon = !!template.icon;
  
  // Get nested campaigns that belong to this template
  const childSprints = nestedInTemplates.filter(ns => ns.parentSprintId === template.id);
  const childOrder = template.childOrder || [];
  const sortedChildren = [...childSprints].sort((a, b) => {
    const indexA = childOrder.indexOf(a.id);
    const indexB = childOrder.indexOf(b.id);
    if (indexA === -1 && indexB === -1) return 0;
    if (indexA === -1) return 1;
    if (indexB === -1) return -1;
    return indexA - indexB;
  });
  const hasChildren = sortedChildren.length > 0;
  const isExpanded = expandedSprints?.has(template.id);

  // Focus input when editing starts
  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  // Close context menu on click outside
  useEffect(() => {
    if (contextMenu) {
      const handleClick = () => setContextMenu(null);
      document.addEventListener('click', handleClick);
      return () => document.removeEventListener('click', handleClick);
    }
  }, [contextMenu]);

  const handleContextMenu = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setContextMenu({ x: e.clientX, y: e.clientY });
  };

  const handleRename = () => {
    setContextMenu(null);
    setEditValue(template.name);
    setIsEditing(true);
  };

  const handleDelete = () => {
    setContextMenu(null);
    setShowDeleteModal(true);
  };

  const confirmDelete = () => {
    setShowDeleteModal(false);
    onDeleteTemplate?.(template.id);
  };

  const handleDuplicate = () => {
    setContextMenu(null);
    onDuplicateTemplate?.(template);
  };

  const handleUse = () => {
    setContextMenu(null);
    onUseTemplate?.(template);
  };

  const handleOpenInNewTab = () => {
    setContextMenu(null);
    onOpenInNewTab?.(template, 'template');
  };

  const saveEdit = () => {
    const trimmed = editValue.trim();
    if (trimmed && trimmed !== template.name) {
      onRenameTemplate?.(template.id, trimmed);
    }
    setIsEditing(false);
  };

  const cancelEdit = () => {
    setIsEditing(false);
    setEditValue(template.name);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      saveEdit();
    } else if (e.key === 'Escape') {
      cancelEdit();
    }
  };

  const handleIconClick = (e) => {
    e.stopPropagation();
    if (iconRef.current) {
      const rect = iconRef.current.getBoundingClientRect();
      setIconPickerPosition({
        x: rect.right + 8,
        y: rect.top,
      });
    }
    setShowIconPicker(true);
  };

  const handleIconChange = (newIcon) => {
    onTemplateIconChange?.(template.id, newIcon);
  };

  const handleCreateNestedSprint = (e) => {
    e.stopPropagation();
    onCreateNestedSprint?.(template.id);
  };

  return (
    <div className="sidebar__tree-item">
      <button
        className={`sidebar__item sidebar__item--template ${isSelected ? 'sidebar__item--selected' : ''} ${hasChildren ? 'sidebar__item--has-children' : ''}`}
        onClick={() => !isEditing && onSelectTemplate?.(template)}
        onContextMenu={handleContextMenu}
      >
        <div className="sidebar__item-row">
          {/* Icon container - shows custom icon or default template icon */}
          <span 
            ref={iconRef}
            className={`sidebar__item-icon-container ${hasIcon ? 'sidebar__item-icon-container--custom' : ''}`}
            onClick={handleIconClick}
            title={hasIcon ? "Click to change icon" : "Click to add icon"}
          >
            <span className="sidebar__item-icon">
              {hasIcon ? (
                renderIcon(template.icon, { 
                  size: '14px',
                  fallback: <TemplateItemIcon size={12} />
                })
              ) : (
                <TemplateItemIcon size={12} />
              )}
            </span>
          </span>
          
          {/* Content wrapper */}
          <div className="sidebar__item-content">
            <div className="sidebar__item-label-row">
              {isEditing ? (
                <input
                  ref={inputRef}
                  type="text"
                  className="sidebar__item-input"
                  value={editValue}
                  onChange={(e) => setEditValue(e.target.value)}
                  onBlur={saveEdit}
                  onKeyDown={handleKeyDown}
                  onClick={(e) => e.stopPropagation()}
                />
              ) : (
                <span className="sidebar__item-label">{template.name}</span>
              )}
              
              {/* Action buttons container - shown on hover */}
              <span className="sidebar__item-actions">
                {/* Add nested campaign button */}
                <span 
                  className="sidebar__item-action-btn"
                  onClick={handleCreateNestedSprint}
                  title="Add nested campaign"
                >
                  <PlusIcon size={9} />
                </span>
                
                {/* Chevron for expandable items */}
                {hasChildren && (
                  <span 
                    className="sidebar__item-chevron"
                    onClick={(e) => {
                      e.stopPropagation();
                      onToggleExpand?.(template.id);
                    }}
                  >
                    <ChevronIcon size={10} expanded={isExpanded} />
                  </span>
                )}
              </span>
            </div>
          </div>
        </div>
      </button>

      {/* Context Menu */}
      {contextMenu && createPortal(
        <div 
          className="sidebar__context-menu"
          style={{ left: contextMenu.x, top: contextMenu.y }}
        >
          <button className="sidebar__context-menu-item" onClick={handleOpenInNewTab}>
            Open in new tab
          </button>
          <button className="sidebar__context-menu-item" onClick={handleUse}>
            Use Template
          </button>
          <div className="sidebar__context-menu-divider" />
          <button className="sidebar__context-menu-item" onClick={handleRename}>
            Rename
          </button>
          <button 
            className="sidebar__context-menu-item" 
            onClick={() => {
              setContextMenu(null);
              setIconPickerPosition({ x: contextMenu.x, y: contextMenu.y });
              setShowIconPicker(true);
            }}
          >
            {hasIcon ? 'Change Icon' : 'Add Icon'}
          </button>
          <button className="sidebar__context-menu-item" onClick={handleDuplicate}>
            Duplicate
          </button>
          <div className="sidebar__context-menu-divider" />
          <button className="sidebar__context-menu-item sidebar__context-menu-item--danger" onClick={handleDelete}>
            <TrashIcon size={12} />
            Move to Trash
          </button>
        </div>,
        document.body
      )}

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        isOpen={showDeleteModal}
        title="Move to Trash"
        message={`Move template "${template.name}" to trash? You can restore it later from the Trash.`}
        confirmLabel="Move to Trash"
        cancelLabel="Cancel"
        confirmVariant="warning"
        icon="🗑"
        onConfirm={confirmDelete}
        onCancel={() => setShowDeleteModal(false)}
      />

      {/* Icon Picker */}
      {showIconPicker && (
        <IconPicker
          currentIcon={template.icon}
          onIconChange={handleIconChange}
          onIconDeleted={() => handleIconChange(null)}
          onClose={() => setShowIconPicker(false)}
          position={iconPickerPosition}
        />
      )}

      {/* Nested Children (campaigns inside this template) */}
      {hasChildren && isExpanded && (
        <div className="sidebar__tree-children">
          {sortedChildren.map((child, idx) => (
            <SprintTreeItem
              key={child.id}
              sprint={child}
              nestedSprints={nestedInTemplates}
              allSprints={nestedInTemplates}
              selectedSprintId={selectedSprintId}
              onSelectSprint={onSelectSprint}
              onRenameSprint={onRenameSprint}
              onSprintIconChange={onSprintIconChange}
              onDeleteSprint={onDeleteSprint}
              onNestCampaign={null}
              onReorderCampaign={null}
              onReorderNestedCampaign={null}
              onCreateNestedSprint={onCreateNestedSprint}
              depth={1}
              expandedSprints={expandedSprints}
              onToggleExpand={onToggleExpand}
              draggedSprintId={null}
              onDragStart={() => {}}
              onDragEnd={() => {}}
              index={idx}
              parentSprintId={template.id}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default TemplateTreeItem;
